import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { basisTerminationService } from "@modules/staff/basisForTermination/services/basisTermination";
import { notifySuccess, notifyError } from "@config/tostifyConfig/tostifyConfig";
import LoadingButton from "@shared/components/LoadingButton";

export default function BasisForTerminationTranslation({ onClose, record, onRefresh }) {
    const { t } = useTranslation();
    const modalRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { register, handleSubmit, formState: { errors }, reset, trigger, setValue, watch } = useForm();

    const watchedValues = watch();

    useEffect(() => {
        const fetchTranslations = async () => {
            if (record) {
                try {
                    setLoading(true);
                    setError(null);
                    
                    setValue('reasonKt', record.reasonKt || '');
                    setValue('description', record.description || '');
                    
                    const translationData = await basisTerminationService.getTranslation(record.id);
                    // Handle both array and object responses
                    let translation = null;
                    if (Array.isArray(translationData) && translationData.length > 0) {
                        translation = translationData[0];
                    } else if (translationData && typeof translationData === 'object') {
                        translation = translationData;
                    }
                    
                    if (translation) {
                        // The API returns translated values in 'transDescription' and 'transReasonKt' fields
                        setValue('transReasonKt', translation.transReasonKt || '');
                        setValue('transDescription', translation.transDescription || '');
                    } else {
                        setValue('transReasonKt', '');
                        setValue('transDescription', '');
                    }
                } catch (err) {
                    console.error('Error fetching translations:', err);
                    setError('Failed to load translations');
                } finally {
                    setLoading(false);
                }
            }
        };

        fetchTranslations();
    }, [record, setValue]);

    const submitForm = async (data) => {
        try {
            setLoading(true);
            const translationData = {
                id: record.id,
                description: data.transDescription || '',
                reasonKt: data.transReasonKt || '',
                langCode: "en_GB"
            };

            await basisTerminationService.saveTranslation(translationData);
            notifySuccess(t('common.translationSuccessfully'));
            if (onRefresh) {
                await onRefresh();
            }
            onClose();
        } catch (error) {
            console.error('Error saving translation:', error);
            const errorMessage = error?.response?.data?.message || error?.response?.data || t('common.errorMessage');
            notifyError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    if (loading && !watchedValues.reasonKt) {
        return (
            <div className="modal show" tabIndex="-1">
                <div className="modal-dialog modal-confirm" ref={modalRef}>
                    <div className="modal-content">
                        <div className="modal-body">
                            <p>{t('common.loading') || 'Loading translations...'}</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="modal show" tabIndex="-1">
            <div className="modal-dialog modal-confirm" ref={modalRef}>
                <div className="modal-content">
                    <button type="button" className="close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                    <div className="modal-body">
                        <h4 className="modal-title">
                            {t('staff.basisForTermination.table.headers.translated') || 'Translations'}
                        </h4>
                        <form id="translationsForm" className="region-form" onSubmit={handleSubmit(submitForm)}>
                            <div className="form-sections">
                                <div className="form-section">
                                    <div className="form-group">
                                        <span className="span-bold">{t('staff.basisForTermination.table.headers.reasonKt') || 'Reason under the Labour Code'}</span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            disabled
                                            {...register('reasonKt')}
                                            value={watchedValues.reasonKt || ''}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <span className="span-bold">{t('staff.basisForTermination.table.headers.description') || 'Description'}</span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            disabled
                                            {...register('description')}
                                            value={watchedValues.description || ''}
                                        />
                                    </div>
                                </div>
                                <div className="form-section">
                                    <div className="form-group">
                                        <span className="span-bold">{t('staff.basisForTermination.table.headers.transReasonKt') || 'Reason under the Labour Code (Translated)'}</span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            maxLength={120}
                                            {...register('transReasonKt')}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <span className="span-bold">{t('staff.basisForTermination.table.headers.transDescription') || 'Description (Translated)'}</span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            maxLength={120}
                                            {...register('transDescription')}
                                        />
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                    <div className="modal-footer">
                        <LoadingButton type="submit" form="translationsForm" className="btn btn-primary" loading={loading} disabled={loading}>
                            {t('common.save')}
                        </LoadingButton>
                        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
                            {t('common.cancel')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

